import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { 
  X, 
  Flashlight, 
  RotateCcw, 
  Square,
  Volume2,
  VolumeX
} from 'lucide-react';
import { useScanditScanner } from '@/hooks/useScanditScanner';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface FullScreenScannerProps {
  isOpen: boolean;
  onClose: () => void;
  onScanResult: (barcode: string) => void;
}

export const FullScreenScanner = ({
  isOpen,
  onClose,
  onScanResult
}: FullScreenScannerProps) => {
  const isMobile = useIsMobile();
  const videoRef = useRef<HTMLDivElement>(null);
  
  const {
    isScanning,
    error,
    isInitialized,
    detectionStrength,
    soundEnabled,
    initializationStep,
    retryCount,
    startScanning,
    stopScanning,
    toggleTorch,
    toggleSound,
    simulateScan,
    retryInitialization
  } = useScanditScanner({
    onScanResult: (barcode) => {
      onScanResult(barcode);
      onClose();
    },
    onClose
  });

  if (!isOpen || !isMobile) return null;

  const handleClose = () => {
    stopScanning();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-modal bg-black">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-black/50 backdrop-blur-sm">
        <div className="flex items-center justify-between p-4 pt-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-white hover:bg-white/20"
          >
            <X className="w-5 h-5" />
          </Button>
          
          <h1 className="text-white font-medium">Scan Barcode</h1>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSound}
            className="text-white hover:bg-white/20"
          >
            {soundEnabled ? (
              <Volume2 className="w-5 h-5" />
            ) : (
              <VolumeX className="w-5 h-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Scanner Area */}
      <div className="relative w-full h-full">
        <div 
          ref={videoRef} 
          className="w-full h-full bg-black"
        />
        
        {/* Scanning Overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative">
            {/* Scanning Frame */}
            <div className="w-64 h-64 border-2 border-primary rounded-lg relative">
              {/* Corner indicators */}
              <div className="absolute top-0 left-0 w-6 h-6 border-l-4 border-t-4 border-white rounded-tl-lg" />
              <div className="absolute top-0 right-0 w-6 h-6 border-r-4 border-t-4 border-white rounded-tr-lg" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-l-4 border-b-4 border-white rounded-bl-lg" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-r-4 border-b-4 border-white rounded-br-lg" />
              
              {/* Scanning line animation */}
              {isScanning && (
                <div className="absolute top-0 left-0 w-full h-1 bg-primary animate-[scan-line_2s_ease-in-out_infinite]" />
              )}
            </div>
            
            {/* Instructions */}
            <div className="mt-4 text-center">
              <p className="text-white text-lg font-medium">
                Align barcode within frame
              </p>
              <p className="text-white/70 text-sm mt-1">
                Hold steady for automatic scanning
              </p>
              
              {detectionStrength > 0 && (
                <div className="mt-2">
                  <div className="w-32 h-2 bg-white/20 rounded-full mx-auto overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${detectionStrength}%` }}
                    />
                  </div>
                  <p className="text-white/70 text-xs mt-1">
                    Detection: {detectionStrength}%
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status Messages */}
        {!isInitialized && (
          <div className="absolute inset-x-4 top-1/2 transform -translate-y-1/2">
            <div className="bg-black/70 backdrop-blur-sm rounded-lg p-4 text-center">
              <p className="text-white font-medium">Initializing Scanner...</p>
              <p className="text-white/70 text-sm mt-1">{initializationStep}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-x-4 top-1/2 transform -translate-y-1/2">
            <div className="bg-destructive/90 backdrop-blur-sm rounded-lg p-4 text-center">
              <p className="text-white font-medium">Scanner Error</p>
              <p className="text-white/90 text-sm mt-1">{error}</p>
              <Button
                variant="secondary"
                size="sm"
                onClick={retryInitialization}
                className="mt-3"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Retry ({retryCount}/3)
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/50 backdrop-blur-sm p-4 pb-8">
        <div className="flex items-center justify-center gap-4">
          {/* Torch Button */}
          <Button
            variant="outline"
            size="lg"
            onClick={toggleTorch}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20 w-16 h-16 rounded-full"
          >
            <Flashlight className="w-6 h-6" />
          </Button>

          {/* Stop/Start Button */}
          {isScanning ? (
            <Button
              variant="destructive"
              size="lg"
              onClick={stopScanning}
              className="w-20 h-16 rounded-full"
            >
              <Square className="w-6 h-6" />
            </Button>
          ) : (
            <Button
              variant="default"
              size="lg"
              onClick={startScanning}
              disabled={!isInitialized}
              className="w-20 h-16 rounded-full"
            >
              Start
            </Button>
          )}

          {/* Simulate Button (Development) */}
          <Button
            variant="outline"
            size="lg"
            onClick={simulateScan}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20 w-16 h-16 rounded-full"
          >
            Test
          </Button>
        </div>
      </div>
    </div>
  );
};